import React, { createContext, useCallback, useContext, useState, useEffect } from "react";
import api, { isSessionEnded, SESSION_ENDED_EVENT } from "@/lib/api";

const AuthContext = createContext(null);

// Session restore retries a transient failure rather than dumping the user at
// the login screen: opening a tab while the backend is restarting mid-deploy is
// not a reason to end a valid session.
const RESTORE_RETRIES = 2;
const RESTORE_RETRY_MS = 1500;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("dermatrix_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    let cancelled = false;

    const restore = async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          const res = await api.get("/auth/me");
          return res.data;
        } catch (err) {
          // A real auth failure is final — the interceptor has already dropped
          // the token. Anything else (offline, 5xx, backend mid-restart) is
          // worth another try before giving up on the session.
          if (isSessionEnded(err)) return false;
          if (attempt >= RESTORE_RETRIES) {
            // Still can't confirm. Show the login screen, but LEAVE the token
            // in place so the next load can restore the session once the
            // backend is reachable again.
            return false;
          }
          await new Promise((r) => setTimeout(r, RESTORE_RETRY_MS));
        }
      }
    };

    restore().then((result) => {
      if (cancelled) return;
      setUser(result);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, []);

  // Login/logout in ANOTHER TAB of this browser. The token in localStorage and
  // the access_token cookie are shared by every tab, but `user` above is this
  // tab's memory. Without this, on a PC two telecallers share, a tab left open
  // kept showing "Laxmi" after Kajal signed in elsewhere — while every request
  // it made went out as Kajal: the wrong leads on screen, and calls/status
  // changes recorded under the other person's name. Reload so the tab
  // re-restores from whatever login is current now (or shows the login page).
  useEffect(() => {
    const onStorage = (e) => {
      // key === null: localStorage.clear() in another tab.
      if ((e.key === "dermatrix_token" && e.newValue !== e.oldValue) || e.key === null) {
        window.location.reload();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // A disabled/blacklisted account mid-session (see the api interceptor): drop
  // to the login screen, which shows the server's reason.
  useEffect(() => {
    const onEnded = () => setUser(false);
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
  }, []);

  const finishLogin = (data) => {
    localStorage.setItem("dermatrix_token", data.token);
    setUser(data.user);
    return data.user;
  };

  // Which sign-in form to show: {otp_login, code_length}. Public endpoint.
  // Stable identity: the login page fetches it in an effect.
  const getLoginConfig = useCallback(async () => {
    const { data } = await api.get("/auth/login-config");
    return data;
  }, []);

  // Password sign-in (only while OTP login is off). Returns the user. An
  // {otp_required, ...} challenge, should a server still send one, is passed
  // back for the login page's code step.
  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    if (data?.otp_required) return data;
    return finishLogin(data);
  };

  // OTP sign-in: mobile number or email in, a challenge out (no password).
  const requestLoginOtp = async (identifier) => {
    const { data } = await api.post("/auth/login/request-otp", { identifier });
    return data;
  };

  const verifyLoginOtp = async (challengeId, code) => {
    const { data } = await api.post("/auth/login/verify-otp", { challenge_id: challengeId, code });
    return finishLogin(data);
  };

  // A fresh code (the previous one stops working); same shape as the challenge.
  const resendLoginOtp = async (challengeId) => {
    const { data } = await api.post("/auth/login/resend-otp", { challenge_id: challengeId });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("dermatrix_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, getLoginConfig, login, requestLoginOtp, verifyLoginOtp, resendLoginOtp, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
